"use client";

/**
 * ROI calculator on /business-value. Pure client-side arithmetic on the
 * visitor's own (editable) assumptions — every number in the output derives
 * from the inputs on screen, and the copy labels the result as an indication.
 *
 * Plan prices are read from `marketing-pricing.ts` (the single source of
 * truth) so the calculator can never drift from the public pricing page.
 * The plan estimate mirrors the pricing page's worked example: roughly two
 * AI actions per ticket (classification + proposed reply) plus one per
 * technical escalation (remediation step).
 */

import { useMemo, useState } from "react";
import { Calculator, CheckCircle2 } from "lucide-react";
import { PRICING_PLANS } from "@/lib/marketing-pricing";
import { useLocale, useT } from "@/i18n";

type Inputs = {
  tickets: number;
  escalationPct: number;
  supportRate: number;
  devRate: number;
  searchMin: number;
  fixHours: number;
  acceptancePct: number;
  fixSavePct: number;
};

const DEFAULTS: Inputs = {
  tickets: 400,
  escalationPct: 15,
  supportRate: 45,
  devRate: 85,
  searchMin: 12,
  fixHours: 4,
  acceptancePct: 60,
  fixSavePct: 50,
};

/** Plan action ceilings mirror the public limits (250 / 1.000 / 5.000). */
const PLAN_ACTION_CEILINGS: Array<{ planId: string; maxActions: number }> = [
  { planId: "starter", maxActions: 250 },
  { planId: "team", maxActions: 1000 },
  { planId: "pro", maxActions: 5000 },
  { planId: "enterprise", maxActions: Number.POSITIVE_INFINITY },
];

function estimateActions(i: Inputs): number {
  return Math.round(i.tickets * 2 + i.tickets * (i.escalationPct / 100));
}

function pickPlan(actions: number) {
  const ceiling = PLAN_ACTION_CEILINGS.find((c) => actions <= c.maxActions) ?? PLAN_ACTION_CEILINGS[PLAN_ACTION_CEILINGS.length - 1];
  return PRICING_PLANS.find((p) => p.id === ceiling.planId) ?? PRICING_PLANS[PRICING_PLANS.length - 1];
}

function compute(i: Inputs) {
  const acceptance = i.acceptancePct / 100;
  const escalations = i.tickets * (i.escalationPct / 100);
  const supportGross = i.tickets * acceptance * (i.searchMin / 60) * i.supportRate;
  const engineeringGross = escalations * acceptance * i.fixHours * (i.fixSavePct / 100) * i.devRate;
  const plan = pickPlan(estimateActions(i));
  // Enterprise has a "from" price; use the monthly floor as the indication.
  const cost = plan.monthly ?? 0;
  const gross = supportGross + engineeringGross;
  const net = gross - cost;
  // Hours saved — same terms as the euro amounts, so the sum rows can say
  // "± N hours" in plain language next to the money.
  const supportHours = i.tickets * acceptance * (i.searchMin / 60);
  const engineeringHours = escalations * acceptance * i.fixHours * (i.fixSavePct / 100);
  const perTicketValue =
    acceptance * (i.searchMin / 60) * i.supportRate +
    (i.escalationPct / 100) * acceptance * i.fixHours * (i.fixSavePct / 100) * i.devRate;
  const breakEvenTickets = perTicketValue > 0 ? Math.ceil(cost / perTicketValue) : null;
  const paybackDays = gross > 0 ? Math.ceil((cost / gross) * 30) : null;
  return {
    supportGross,
    engineeringGross,
    supportHours,
    engineeringHours,
    cost,
    net,
    plan,
    breakEvenTickets,
    paybackDays,
    gross,
  };
}

const FIELDS: Array<{ key: keyof Inputs; min: number; max: number; step: number; suffix?: "%" }> = [
  { key: "tickets", min: 0, max: 100000, step: 10 },
  { key: "escalationPct", min: 0, max: 100, step: 1, suffix: "%" },
  { key: "supportRate", min: 0, max: 500, step: 5 },
  { key: "devRate", min: 0, max: 500, step: 5 },
  { key: "searchMin", min: 0, max: 240, step: 1 },
  { key: "fixHours", min: 0, max: 80, step: 0.5 },
  { key: "acceptancePct", min: 0, max: 100, step: 5, suffix: "%" },
  { key: "fixSavePct", min: 0, max: 100, step: 5, suffix: "%" },
];

export function RoiCalculator() {
  const t = useT();
  const { locale } = useLocale();
  const [inputs, setInputs] = useState<Inputs>(DEFAULTS);
  const r = useMemo(() => compute(inputs), [inputs]);

  const euro = (v: number) =>
    new Intl.NumberFormat(locale === "nl" ? "nl-NL" : "en-US", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(Math.round(v));

  const hrs = (v: number) =>
    new Intl.NumberFormat(locale === "nl" ? "nl-NL" : "en-US", { maximumFractionDigits: 0 }).format(
      Math.round(v),
    );

  const set = (key: keyof Inputs, raw: string, min: number, max: number) => {
    const v = Number(raw);
    if (!Number.isFinite(v)) return;
    setInputs((prev) => ({ ...prev, [key]: Math.min(max, Math.max(min, v)) }));
  };

  return (
    <div className="mt-12 grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* Inputs */}
      <div className="rounded-2xl border border-line-subtle bg-bg-raised p-6">
        <h3 className="text-[15px] font-semibold text-fg-bright">{t("businessValuePage.calc.inputsTitle")}</h3>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="text-[12.5px] font-medium text-fg">
                {t(`businessValuePage.calc.inputs.${f.key}.label`)}
              </span>
              <span className="relative mt-1.5 block">
                <input
                  type="number"
                  inputMode="decimal"
                  min={f.min}
                  max={f.max}
                  step={f.step}
                  value={inputs[f.key]}
                  onChange={(e) => set(f.key, e.target.value, f.min, f.max)}
                  className="w-full rounded-lg border border-line-subtle bg-bg-elev px-3 py-2 text-[14px] text-fg-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                />
                {f.suffix && (
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[13px] text-fg-muted">
                    {f.suffix}
                  </span>
                )}
              </span>
              {t(`businessValuePage.calc.inputs.${f.key}.hint`) && (
                <span className="mt-1 block text-[11.5px] leading-snug text-fg-muted">
                  {t(`businessValuePage.calc.inputs.${f.key}.hint`)}
                </span>
              )}
            </label>
          ))}
        </div>
      </div>

      {/* Outputs — one plain answer ("what do I save?"), then the sum that
          produces it, then break-even/payback as readable sentences. */}
      <div className="rounded-2xl border border-brand-500/30 bg-bg-raised p-6">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-bg-elev text-brand-300 border border-line-subtle">
            <Calculator size={16} />
          </span>
          <h3 className="text-[15px] font-semibold text-fg-bright">{t("businessValuePage.calc.outputsTitle")}</h3>
        </div>

        {/* The answer. */}
        <div className="mt-5 rounded-xl border border-line-subtle bg-bg-elev p-5 text-center">
          <div className="text-[12px] font-medium uppercase tracking-[0.14em] text-fg-muted">
            {t("businessValuePage.calc.headlineLabel")}
          </div>
          <div className={`mt-1.5 text-[34px] font-semibold leading-none ${r.net >= 0 ? "text-status-ok" : "text-status-high"}`}>
            {euro(r.net)}
          </div>
          <div className="mt-1.5 text-[13px] text-fg-dim">{t("businessValuePage.calc.perMonth")}</div>
          {r.net > 0 && (
            <div className="mt-1 text-[12.5px] text-fg-muted">
              {t("businessValuePage.calc.perYear", { amount: euro(r.net * 12) })}
            </div>
          )}
        </div>

        {/* The sum behind it, in plain language with hours next to euros. */}
        <div className="mt-6 text-[12px] font-medium uppercase tracking-[0.14em] text-fg-muted">
          {t("businessValuePage.calc.sumTitle")}
        </div>
        <dl className="mt-3 space-y-2.5">
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-[13px] leading-snug text-fg-dim">
              {t("businessValuePage.calc.rows.support", { hours: hrs(r.supportHours) })}
            </dt>
            <dd className="whitespace-nowrap text-[14px] font-semibold text-fg-bright">+ {euro(r.supportGross)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-[13px] leading-snug text-fg-dim">
              {t("businessValuePage.calc.rows.engineering", { hours: hrs(r.engineeringHours) })}
            </dt>
            <dd className="whitespace-nowrap text-[14px] font-semibold text-fg-bright">+ {euro(r.engineeringGross)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-4 border-b border-line-subtle pb-2.5">
            <dt className="text-[13px] leading-snug text-fg-dim">
              {t("businessValuePage.calc.rows.cost", { plan: t(r.plan.name) })}
            </dt>
            <dd className="whitespace-nowrap text-[14px] font-semibold text-fg-bright">
              − {r.plan.id === "enterprise" ? `${t("pricingPage.tiers.from")} ${euro(r.cost)}` : euro(r.cost)}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-[13.5px] font-semibold text-fg-bright">{t("businessValuePage.calc.rows.net")}</dt>
            <dd className={`whitespace-nowrap text-[15px] font-semibold ${r.net >= 0 ? "text-status-ok" : "text-status-high"}`}>
              {euro(r.net)}
            </dd>
          </div>
        </dl>

        {/* Break-even + payback as sentences a non-accountant can read. */}
        <ul className="mt-5 space-y-1.5">
          {r.net >= 0 && r.breakEvenTickets !== null && (
            <li className="flex items-start gap-2 text-[13px] leading-relaxed text-fg-dim">
              <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-status-ok" />
              {t("businessValuePage.calc.breakEvenPlain", {
                tickets: new Intl.NumberFormat(locale === "nl" ? "nl-NL" : "en-US").format(r.breakEvenTickets),
              })}
            </li>
          )}
          {r.net >= 0 && r.paybackDays !== null && (
            <li className="flex items-start gap-2 text-[13px] leading-relaxed text-fg-dim">
              <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-status-ok" />
              {t("businessValuePage.calc.paybackPlain", { days: String(Math.min(30, r.paybackDays)) })}
            </li>
          )}
          {r.net < 0 && (
            <li className="text-[13px] leading-relaxed text-fg-dim">
              {t("businessValuePage.calc.negativePlain")}
            </li>
          )}
        </ul>

        <p className="mt-5 text-[12px] leading-relaxed text-fg-muted">
          {t("businessValuePage.calc.planNote", { plan: t(r.plan.name) })}
        </p>
        <p className="mt-2 text-[12px] leading-relaxed text-fg-muted">{t("businessValuePage.calc.disclaimer")}</p>
      </div>
    </div>
  );
}
